import { z } from 'zod';

import type { CountryCode } from '@apple/utils/globalization/models';
import { countryCodeSchema, dateTimeSchema } from '@apple/utils/globalization/models';

import { type ProductKey, productKeySchema } from '@apple/features/product/models/product';
import type { StoreEventType } from '@apple/features/store-events/models/shared';
import { storeEventTypeSchema } from '@apple/features/store-events/models/shared';

export type StoreEventRuleType = 'global' | 'plant' | 'country' | 'market';

export interface StoreEventProductRule {
	itemId: ProductKey;
	storeEventCategoryId: string;
	eventType: StoreEventType;
	defaultQuantity: number;
	updatedDate?: string;
}

/**
 * @see AppleRetail.Data.StoreEvents.StoreEventCategoryProductGlobalAssociation
 */
export interface GlobalStoreEventRule extends StoreEventProductRule {}

/**
 * @see AppleRetail.Data.StoreEvents.StoreEventCategoryProductPlantAssociation
 */
export interface PlantStoreEventRule extends StoreEventProductRule {
	warehouseLocationId: number;
}

/**
 * @see AppleRetail.Data.StoreEvents.StoreEventCategoryProductCountryAssociation
 */
export interface CountryStoreEventRule extends StoreEventProductRule {
	countryCode: CountryCode;
}

/**
 * @see AppleRetail.Data.StoreEvents.StoreEventCategoryProductCustomerGroupAssociation
 */
export interface MarketStoreEventRule extends StoreEventProductRule {
	customerGroupId: number;
}

/**
 * @see AppleRetail.Data.StoreEvents.StoreEventProductConfiguration
 */
export interface StoreEventProductConfiguration {
	itemId: ProductKey;
	isEnabled: boolean;
	updatedDate?: Date | null;
	globalAssociations: GlobalStoreEventRule[];
	plantAssociations: PlantStoreEventRule[];
	countryAssociations: CountryStoreEventRule[];
	customerGroupAssociations: MarketStoreEventRule[];
}

/**
 * Used in the form to create store event rules for a product
 */
export interface StoreEventRule {
	eventType: StoreEventType;
	category: string;
	accessType: StoreEventRuleType;
	configuration: string | null;
	selectedCountryCodes: CountryCode[];
	quantity: number;
}

export const storeEventRuleTypeSchema = z.enum([
	'global',
	'plant',
	'country',
	'market',
]) satisfies z.ZodType<StoreEventRuleType>;

export const storeEventProductRuleSchema = z.object({
	itemId: productKeySchema,
	storeEventCategoryId: z.string(),
	eventType: storeEventTypeSchema,
	defaultQuantity: z.number(),
	updatedDate: dateTimeSchema.nullable().optional(),
});

/**
 * @see AppleRetail.Data.StoreEvents.StoreEventCategoryProductGlobalAssociation
 */
export const globalStoreEventRuleSchema =
	storeEventProductRuleSchema as z.ZodType<GlobalStoreEventRule>;

/**
 * @see AppleRetail.Data.StoreEvents.StoreEventCategoryProductPlantAssociation
 */
export const plantStoreEventRuleSchema = storeEventProductRuleSchema.extend({
	warehouseLocationId: z.number(),
}) as z.ZodType<PlantStoreEventRule>;

/**
 * @see AppleRetail.Data.StoreEvents.StoreEventCategoryProductCountryAssociation
 */
export const countryStoreEventRuleSchema = storeEventProductRuleSchema.extend({
	countryCode: countryCodeSchema,
}) as z.ZodType<CountryStoreEventRule>;

/**
 * @see AppleRetail.Data.StoreEvents.StoreEventCategoryProductCustomerGroupAssociation
 */
export const marketStoreEventRuleSchema = storeEventProductRuleSchema.extend({
	customerGroupId: z.number(),
}) as z.ZodType<MarketStoreEventRule>;

/**
 * @see AppleRetail.Data.StoreEvents.StoreEventProductConfiguration
 */
export const storeEventProductConfigurationSchema = z.object({
	itemId: productKeySchema,
	isEnabled: z.boolean(),
	updatedDate: dateTimeSchema.nullable().optional(),
	globalAssociations: z.array(globalStoreEventRuleSchema),
	plantAssociations: z.array(plantStoreEventRuleSchema),
	countryAssociations: z.array(countryStoreEventRuleSchema),
	customerGroupAssociations: z.array(marketStoreEventRuleSchema),
}) as z.ZodType<StoreEventProductConfiguration>;

/**
 * Used in the form to create store event rules for a product
 */
export const storeEventRuleSchema = (configurationMessage: string, plantCountriesMessage: string) =>
	z
		.object({
			eventType: storeEventTypeSchema,
			category: z.string().min(1),
			accessType: storeEventRuleTypeSchema,
			configuration: z.string().nullable(),
			selectedCountryCodes: countryCodeSchema.array(),
			quantity: z.coerce.number().min(1),
		})
		.superRefine((data, ctx) => {
			if (data.accessType !== 'global' && (data.configuration ?? '') === '') {
				ctx.addIssue({
					code: 'custom',
					message: configurationMessage,
					path: ['configuration'],
				});
			}

			if (data.accessType === 'plant' && data.selectedCountryCodes.length === 0) {
				ctx.addIssue({
					code: 'custom',
					message: plantCountriesMessage,
					path: ['configuration'],
				});
			}
		}) satisfies z.ZodType<StoreEventRule>;

/** Determines if two rules are equivalent. */
export function compareFormToRule<T extends StoreEventProductRule>(
	a: T,
	b: StoreEventRule,
	configField?: keyof T,
): boolean {
	return (
		a.storeEventCategoryId === b.category &&
		a.eventType === b.eventType &&
		(configField === undefined || a[configField]?.toString() === (b.configuration as unknown))
	);
}

export function compareRules<T extends StoreEventProductRule>(
	a: T,
	b: T,
	configField?: keyof T,
): boolean {
	return (
		a.storeEventCategoryId === b.storeEventCategoryId &&
		a.eventType === b.eventType &&
		(configField === undefined || a[configField]?.toString() === b[configField]?.toString())
	);
}

export function compareRulesByType(a: StoreEventProductRule, b: StoreEventProductRule): boolean {
	if (isPlantRule(a) && isPlantRule(b)) {
		return compareRules(a, b, 'warehouseLocationId');
	}
	if (isCountryRule(a) && isCountryRule(b)) {
		return compareRules(a, b, 'countryCode');
	}
	if (isMarketRule(a) && isMarketRule(b)) {
		return compareRules(a, b, 'customerGroupId');
	}
	return compareRules(a, b);
}

export function isPlantRule(rule: StoreEventProductRule): rule is PlantStoreEventRule {
	return plantStoreEventRuleSchema.safeParse(rule).success;
}

export function isCountryRule(rule: StoreEventProductRule): rule is CountryStoreEventRule {
	return countryStoreEventRuleSchema.safeParse(rule).success;
}

export function isMarketRule(rule: StoreEventProductRule): rule is MarketStoreEventRule {
	return marketStoreEventRuleSchema.safeParse(rule).success;
}

export function isGlobalRule(rule: StoreEventProductRule): rule is GlobalStoreEventRule {
	return (
		!isPlantRule(rule) &&
		!isCountryRule(rule) &&
		!isMarketRule(rule) &&
		globalStoreEventRuleSchema.safeParse(rule).success
	);
}
